'use client';
import {
  Address,
  EthBalance,
  Identity,
  Name,
} from '@coinbase/onchainkit/identity';
import {
  ConnectWallet,
  Wallet,
  WalletDropdown,
  WalletDropdownDisconnect,
} from '@coinbase/onchainkit/wallet';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import Link from 'next/link';
import { memo, useCallback, useMemo } from 'react';
import { useMobileMenu } from 'src/context/MobileMenuContext';
import { useProfile } from 'src/context/ProfileContext';
import { useAccount } from 'wagmi';

type WalletWrapperParams = {
  text?: string;
  className?: string;
  withWalletAggregator?: boolean;
  profileName?: string;
  profileAvatar?: string;
  isSwapButton?: boolean;
  onSwapClick?: () => void;
};

const _DefaultAvatar = () => (
  <div className="h-6 w-6">
    <img src="/favicon.ico" alt="Default Avatar" className="h-full w-full" />
  </div>
);

const WalletWrapper = memo(
  ({
    className,
    text,
    profileName,
    profileAvatar,
    isSwapButton,
    onSwapClick,
  }: WalletWrapperParams) => {
    const { address, isConnected } = useAccount();
    const { profile, isProfileLoading } = useProfile();
    const { setIsMenuOpen } = useMobileMenu();

    // Use either the passed profileName or the fetched profile's username
    const displayName = useMemo(
      () => profileName || profile?.username,
      [profileName, profile?.username],
    );

    const displayAvatar = useMemo(
      () => profileAvatar || profile?.avatar_url,
      [profileAvatar, profile?.avatar_url],
    );

    const handleMenuClose = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsMenuOpen(false);
      },
      [setIsMenuOpen],
    );

    //console.log('Display values:', { displayName, displayAvatar });

    // console.log('Wallet connected:', isConnected);
    // console.log('Current address:', address);
    // console.log('Current profile:', profile);

    const CustomAvatar = () => (
      <div className="h-6 w-6">
        <img
          src={displayAvatar || '/favicon.ico'}
          alt="Profile Avatar"
          className="h-full w-full rounded-full object-cover"
          onError={(e) => {
            e.currentTarget.src = '/favicon.ico';
          }}
        />
      </div>
    );

    // Add debugging
    //console.log('WalletWrapper props:', { profileName, profileAvatar });

    const dropdownStyles = {
      base: 'bg-gray-800/90 backdrop-blur-sm border border-gray-700/50 rounded-xl shadow-xl',
      identity: 'px-4 pt-3 pb-2 border-b border-gray-700/50',
      item: 'block w-full hover:bg-gray-700/50 cursor-pointer transition-colors duration-200',
      itemContent: 'flex items-center gap-2 px-4 py-2',
      disconnectButton:
        'w-full text-left px-4 py-2 text-red-400 hover:bg-red-500/20 transition-colors duration-200',
    };

    if (!isConnected) {
      return (
        <ConnectButton.Custom>
          {({ account, chain, openConnectModal, mounted }) => {
            const ready = mounted;

            return (
              <div
                {...(!ready && {
                  'aria-hidden': true,
                  style: {
                    opacity: 0,
                    pointerEvents: 'none',
                    userSelect: 'none',
                  },
                })}
                className="w-full"
              >
                <button
                  onClick={openConnectModal}
                  type="button"
                  className={`flex w-full items-center justify-center space-x-2 rounded-lg bg-gray-800 px-4 py-2 font-semibold text-white transition-colors duration-200 hover:bg-gray-700 ${className}`}
                >
                  <span>{text || 'Connect Wallet'}</span>
                </button>
              </div>
            );
          }}
        </ConnectButton.Custom>
      );
    }

    if (isConnected) {
      if (isSwapButton && onSwapClick) {
        return (
          <button onClick={onSwapClick} className={className}>
            Swap{' '}
            <svg
              className='ml-1 inline-block h-4 w-4'
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        );
      }
      return (
        <Wallet>
          <ConnectWallet
            text={text}
            className={`${className} bg-gray-800 text-white transition-colors duration-200 hover:bg-gray-700`}
          >
            <CustomAvatar />
            {displayName ? (
              <span className='max-w-[120px] truncate text-white'>
                {displayName}
              </span>
            ) : (
              <Name className="text-white" />
            )}
          </ConnectWallet>
          <WalletDropdown className={dropdownStyles.base}>
            <Identity
              className={dropdownStyles.identity}
              hasCopyAddressOnClick={true}
            >
              <CustomAvatar />
              {displayName ? (
                <span className="font-medium text-white">{displayName}</span>
              ) : (
                <Name className="text-white" />
              )}
              <Address className="text-gray-400 text-sm" />
              <EthBalance className='font-medium text-green-400' />
            </Identity>

            <Link
              href="/profile"
              className={dropdownStyles.item}
              onClick={handleMenuClose}
            >
              <div className={dropdownStyles.itemContent}>
                <svg
                  className='h-5 w-5 text-gray-400'
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                <span className="text-white">Profile</span>
              </div>
            </Link>

            <Link
              href="/portfolio"
              className={dropdownStyles.item}
              onClick={handleMenuClose}
            >
              <div className={dropdownStyles.itemContent}>
                <svg
                  className='h-5 w-5 text-gray-400'
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                  />
                </svg>
                <span className="text-white">Portfolio</span>
              </div>
            </Link>

            <WalletDropdownDisconnect
              className={dropdownStyles.disconnectButton}
            />
          </WalletDropdown>
        </Wallet>
      );
    }
  },
);

WalletWrapper.displayName = 'WalletWrapper';

export default WalletWrapper;
